import { Stack as ExpoStack, router } from 'expo-router';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import {
  ActivityIndicator,
  FlatList,
  RefreshControl,
  StyleSheet,
  View,
} from 'react-native';

import { ActivityRow } from '@/components/ActivityRow';
import { BackChevron } from '@/components/BackChevron';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Heading } from '@/components/Heading';
import { Inline } from '@/components/Inline';
import { Screen } from '@/components/Screen';
import { Skeleton } from '@/components/Skeleton';
import { Stack } from '@/components/Stack';
import { colors, spacing } from '@/constants/theme';
import { earningsStore } from '@/stores/earnings.store';
import { type Earning } from '@/types/earning';

// Stack route, not a tab. Drilled into from Home's "Recent Activity > View
// all →" link. Read-only paginated history.
//
// Row shape matches Home Recent Activity (slim <ActivityRow> with hairline
// dividers between siblings). No outer card chrome on the list — the screen
// IS the feed, like Twitter/Instagram timelines. Home wraps rows in a Card
// because Recent Activity is a SECTION inside a longer page; here it isn't.

const EarningsListScreen = observer(() => {
  useEffect(() => {
    void earningsStore.ensureLoaded();
  }, []);

  const earnings = earningsStore.earnings;

  const showInitialLoading =
    earningsStore.refreshing && earnings.length === 0 && !earningsStore.loaded;

  const showError =
    !!earningsStore.refreshError && earnings.length === 0;

  const showGlobalEmpty =
    !showInitialLoading &&
    !showError &&
    earningsStore.loaded &&
    earnings.length === 0;

  return (
    <>
      <ExpoStack.Screen options={{ headerShown: false }} />
      <Screen edges={['top']} padding="none" scroll={false}>
        <Inline gap="md" align="center" style={styles.headerRow}>
          <BackChevron />
          <Heading size="2xl">
            Earnings
          </Heading>
        </Inline>

        {showInitialLoading ? (
          <SkeletonList />
        ) : showError ? (
          <View style={styles.statePadding}>
            <ErrorState
              title="Couldn't load earnings"
              body="Couldn't load your earnings. Pull down to refresh, or tap retry."
              onRetry={() => earningsStore.refresh()}
            />
          </View>
        ) : showGlobalEmpty ? (
          <View style={styles.statePadding}>
            <EmptyState
              title="No earnings yet"
              body="Start with a deal — every dollar earned lands here."
              cta={{ label: 'Browse Deals', onPress: () => router.push('/deals') }}
            />
          </View>
        ) : (
          <FlatList
            data={earnings}
            renderItem={renderActivityRow(earnings)}
            keyExtractor={keyExtractor}
            contentContainerStyle={styles.listContent}
            refreshControl={
              <RefreshControl
                refreshing={earningsStore.refreshing}
                onRefresh={() => earningsStore.refresh()}
                tintColor={colors.deepGreen}
              />
            }
            onEndReached={() => earningsStore.loadMore()}
            onEndReachedThreshold={0.4}
            ListFooterComponent={
              earningsStore.loadingMore ? (
                <ActivityIndicator
                  color={colors.deepGreen}
                  style={styles.footer}
                  size="small"
                />
              ) : null
            }
          />
        )}
      </Screen>
    </>
  );
});

export default EarningsListScreen;

// Closure over `data` so each row can decide whether to render the
// hairline divider below it (skip on the last row).
function renderActivityRow(data: Earning[]) {
  return function ItemRenderer({ item, index }: { item: Earning; index: number }) {
    return <ActivityRow earning={item} divider={index < data.length - 1} />;
  };
}

function keyExtractor(e: Earning) {
  return String(e.id);
}

function SkeletonList() {
  return (
    <Stack gap="sm" testID="earnings-list-skeleton" style={styles.skeletonContainer}>
      {[0, 1, 2, 3, 4].map((i) => (
        <View key={i} style={styles.skeletonRow}>
          <Inline gap="md" align="center">
            <Skeleton width={36} height={36} radius="base" />
            <Stack flex={1} gap="xs">
              <Skeleton width="60%" height={16} />
              <Skeleton width="40%" height={12} />
            </Stack>
            <Skeleton width={56} height={28} />
          </Inline>
        </View>
      ))}
    </Stack>
  );
}

const styles = StyleSheet.create({
  headerRow: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
  },
  listContent: {
    paddingBottom: spacing['3xl'],
  },
  statePadding: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.xl,
  },
  skeletonContainer: {
    paddingHorizontal: spacing.lg,
  },
  skeletonRow: {
    paddingVertical: 14,
    paddingHorizontal: spacing.base,
  },
  footer: {
    paddingVertical: spacing.lg,
  },
});
